import { parseJson, deepPush, touch } from 'helpers/transforms'
import { toHtml } from 'libraries/front-end-core/helpers/xml'
import { map, forEach, mapValues } from 'libraries/lodash'
import debug from 'libraries/front-end-core/core/debug'
import { AOL_CODE_BY_DISPLAY_NAME } from 'constants/aol/aol'

export const toContent = function (data) {
  debug.time('toContent')
  data = parseJson(data)
  console.time('dispatch')
  console.timeEnd('dispatch')
  const questions = {}
  const sections = {}
  const qpush = deepPush.bind(null, sections)
  forEach(data, q => {
    const { sectionId, bookmarkId, questionNumber } = mapValues(
      q,
      String
    )
    qpush(sectionId, bookmarkId)
    questions[bookmarkId] = {
      question: toHtml(q.question, 'span', false, ['question']),
      // TODO: investigate if using toHtml method here, instead of when answer is rendered, is optimal
      answer: toHtml(q.answer),
      tocId: bookmarkId,
      sectionId,
      questionNumber
    }
  })
  debug.timeEnd('toContent')
  return { questions, sections }
}

export const toInstantSearch = function (data) {
  data = parseJson(data)
  const results = (data || []).map(item => {
    const question = toHtml(
      `<search-result-question>${item.questionExcerpt}</search-result-question>`
    )
    const answer = data.answerExcerpt
      ? toHtml(
      `<search-result-answer>${item.answerExcerpt}</search-result-answer>`
      )
      : undefined
    return {
      question,
      answer,
      answerExcerpt: item.answerExcerpt,
      sectionId: item.parent_id.toString(),
      questionExcerpt: item.questionExcerpt,
      questionNumber: item.question_num.toString(),
      tocId: item.bookmarkId || item.tocID
    }
  })

  return { results }
}

export const toQuestions = function (data) {
  const content = parseJson(data)
  const nodes = {}
  const sections = {}
  const all = []
  content.forEach(
    ({ parent_id: sectionId, id, questionNumber, title: question }) => {
      touch(sections, [sectionId], []).push(id)
      nodes[id] = {
        sectionId,
        id,
        questionNumber,
        question
      }
      all.push(id)
    }
  )
  return { all, nodes, sections }
}

export const toQAReferences = function (data) {
  return toContent(data)
}

export const toCaseRefs = function (data) {
  const caseRefs = parseJson(data)
  const caseRefMap = {}
  forEach(caseRefs, ({ ncnId, url, active }) => {
    if (url && active) {
      caseRefMap[ncnId] = url
    }
  })
  return caseRefMap
}

const getParameterName = function (name) {
  switch (name.toLowerCase()) {
    case 'sections':
      return 's'
    case 'legislation':
      return 'l'
    case 'cases':
      return 'c'
    default:
      return 'x'
  }
}

export const toFacet = function (item) {
  const isAolFacet = item.parameterName === 'aol'
  const toValue = name => isAolFacet ? (AOL_CODE_BY_DISPLAY_NAME[name] ?? name) : name

  const selectedValues = {}
  const selected = map(item.selected || [], name => {
    const value = toValue(name)
    selectedValues[value.toLowerCase()] = 1
    return { selected: true, name, value, parentId: 0 }
  })
  const available = map(item.availableFacets || [], ({ name, disabled }) => ({
    selected: false,
    name,
    value: toValue(name),
    parentId: 0,
    disabled
  })).filter(item => !selectedValues[item.value.toLowerCase()])

  const options = Array.of(...selected, ...available).filter(v => !!v.value)

  return {
    name: item.name,
    collapsed: selected.length > 0,
    // We want the aol ordering to remain consistent, other facets are ordered by selected
    options: isAolFacet
      ? [...options].sort((a, b) => a.name.localeCompare(b.name))
      : options,
    parameterName: item.parameterName || getParameterName(item.name)
  }
}

const toSearchResult = function (data) {
  const question = toHtml(
    `<search-result-question>${data.questionExcerpt}</search-result-question>`
  )
  const answer = data.answerExcerpt
    ? toHtml(
    `<search-result-answer>${data.answerExcerpt}</search-result-answer>`
    )
    : undefined
  return {
    aol: data.areaOfLaw,
    question,
    answer,
    sectionId: data.parent_id.toString(),
    questionNumber: data.question_num.toString(),
    tocId: data.bookmarkId || data.tocID
  }
}

export const toSearch = function (data) {
  data = parseJson(data)
  return {
    resultCount: data.totalNumberResults,
    results: map(data.results || [], toSearchResult),
    facets: map(data.facets || [], toFacet)
  }
}

export const toSections = function (data) {
  debug.time('toSections')
  data = parseJson(data)

  const nodes = {}
  let root

  const ntouch = touch.bind(null, nodes)
  const npush = deepPush.bind(null, nodes)

  forEach(data, (r, index) => {
    const {
      id,
      title,
      overview = null,
      parentId,
      areaOfLaw
    } = mapValues(r, String)
    const {
      currentUserCanAccessSection,
      currentUserRestrictedQaAccess
    } = r
    if (index === 0) {
      root = id
      ntouch(id, { id, title, overview, path: [], childSections: [], descendants: [], areaOfLaw, parentId })
    } else if (id && Object.prototype.hasOwnProperty.call(nodes, parentId)) {
      const parentPath = nodes[parentId].path
      const path = [...parentPath, id]

      ntouch(id, {
        id,
        title,
        overview: overview ? toHtml(overview) : overview,
        active: !!r.active,
        currentUserCanAccessSection,
        currentUserRestrictedQaAccess,
        path,
        childSections: [],
        descendants: [id],
        areaOfLaw,
        parentId
      })
      npush([parentId, 'childSections'], id)
      parentPath.forEach(p => npush([p, 'descendants'], id))
    }
  })

  debug.timeEnd('toSections')

  return { nodes, root }
}

export const toAnswer = (data) => {
  const answers = parseJson(data)
  return answers.map(({ answer }) => ({
    answer: toHtml(answer)
  }))
}
